import { SatelliteKitchen, SatelliteKitchenData } from './index';
import { ReadCollectionGql } from './gql';

type Data = {
  documents: SatelliteKitchenData[];
  nextToken: string;
};

class SatelliteKitchenCollection {

  readonly centralKitchenId: string;
  readonly documents: SatelliteKitchen[];
  readonly nextToken: string;

  constructor(centralKitchenId: string = '', data: Data = { documents: [], nextToken: '' }) {
    this.centralKitchenId = centralKitchenId;
    this.documents = data.documents.map(it => new SatelliteKitchen(it));
    this.nextToken = data.nextToken;
  }

  async readAll(): Promise<this> {
    let collection = this;
    while (collection.nextToken) {
      const { centralKitchenId, nextToken } = collection;
      if (!centralKitchenId) continue;
      const gql = await new ReadCollectionGql().fetch({ centralKitchenId, nextToken });
      if (!gql.result) throw new Error('invalid result');
      collection = collection.merge(new SatelliteKitchenCollection(centralKitchenId, gql.result));
    }
    return collection;
  }

  merge(collection: SatelliteKitchenCollection): this {
    const documents = SatelliteKitchen.sort([...this.documents, ...collection.documents]);
    const nextToken = collection.nextToken;
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents, nextToken });
  }

  apply(franchisor: SatelliteKitchen): this {
    return this.documents.some(it => it.id === franchisor.id) ? this.replace(franchisor) : this.add(franchisor);
  }

  add(franchisor: SatelliteKitchen): this {
    const documents = SatelliteKitchen.sort([...this.documents, franchisor]);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  replace(franchisor: SatelliteKitchen): this {
    const documents = SatelliteKitchen.sort(this.documents.map(it => it.id === franchisor.id ? franchisor : it));
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  remove(franchisor: SatelliteKitchen): this {
    const documents = this.documents.filter(it => it.id !== franchisor.id);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  toJSON(): Data {
    const { nextToken } = this;
    const documents = this.documents.map(it => it.toJSON());
    return { documents, nextToken };
  }

  static async read(centralKitchenId: string): Promise<SatelliteKitchenCollection> {
    const gql = await new ReadCollectionGql().fetch({ centralKitchenId });
    if (!gql.result) throw new Error('invalid result');
    return new SatelliteKitchenCollection(centralKitchenId, gql.result);
  }

}

export { SatelliteKitchenCollection };
export type { Data as SatelliteKitchenCollectionData };